'use client'

// Interactive Section C accordion. Three stacked cards (Safety,
// Institutions, Sustainability) — clicking a header expands it with
// a GSAP height animation, the lime accent rail snaps in, the
// compliance meter fills, and the checklist items tick in one after
// another. Clicking again collapses. Only one open at a time.

import { useState, useRef, useEffect } from 'react'
import gsap from 'gsap'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'

const EARTH    = '#513205'
const EARTH_70 = 'rgba(81,50,5,0.70)'
const EARTH_50 = 'rgba(81,50,5,0.50)'
const EARTH_15 = 'rgba(81,50,5,0.15)'
const OLIVE    = '#5B7005'
const LIME     = '#9FAF08'
const FONT_BODY  = 'var(--font-body)'
const FONT_DISP  = 'var(--font-display)'

export interface PillarData {
  label: string
  title: string
  body: string
  icon: React.ReactNode
  bullets: string[]
  metric: string
  metricLabel: string
}

interface Props {
  pillars: PillarData[]
  defaultActive?: number
}

export default function SafetyAccordion({ pillars, defaultActive = 0 }: Props) {
  const [active, setActive] = useState<number>(defaultActive)
  const reduced = useReducedMotion()

  return (
    <div className="safety-accordion">
      <style>{`
        .safety-accordion {
          display: flex; flex-direction: column;
          gap: 14px;
          margin: clamp(40px, 6vh, 72px) 0;
        }
        .safety-card {
          position: relative;
          background: rgba(255,255,255,0.32);
          border: 1px solid ${EARTH_15};
          border-radius: 18px;
          overflow: hidden;
          transition: border-color 350ms ease, background 350ms ease,
                      transform 350ms cubic-bezier(0.22,1,0.36,1),
                      box-shadow 350ms ease;
        }
        .safety-card.active {
          border-color: ${LIME};
          background: rgba(255,255,255,0.6);
          box-shadow: 0 14px 44px rgba(159,175,8,0.10), 0 2px 0 rgba(159,175,8,0.18);
        }
        .safety-card:hover:not(.active) {
          border-color: rgba(91,112,5,0.5);
          transform: translateY(-2px);
          background: rgba(255,255,255,0.45);
        }
        .safety-accent {
          position: absolute;
          top: 0; left: 0; bottom: 0;
          width: 3px;
          background: ${LIME};
          transform: scaleY(0);
          transform-origin: top;
          transition: transform 480ms cubic-bezier(0.22,1,0.36,1);
        }
        .safety-card.active .safety-accent { transform: scaleY(1); }

        .safety-head {
          display: flex; align-items: center; gap: clamp(16px, 2vw, 24px);
          padding: clamp(20px, 2.4vw, 28px);
          width: 100%; cursor: pointer;
          background: transparent; border: 0; text-align: left;
          font-family: inherit;
        }
        .safety-head:focus-visible { outline: 2px solid ${LIME}; outline-offset: 2px; border-radius: 18px; }
        .safety-head-icon {
          color: ${OLIVE};
          flex-shrink: 0;
          transition: color 320ms ease, transform 360ms cubic-bezier(0.22,1,0.36,1);
        }
        .safety-card.active .safety-head-icon {
          color: ${LIME};
          transform: scale(1.06) rotate(-3deg);
        }
        .safety-head-meta {
          flex: 1;
          display: flex; flex-direction: column; gap: 6px;
          min-width: 0;
        }
        .safety-head-label {
          font-family: ${FONT_BODY};
          font-size: 10.5px;
          letter-spacing: 0.36em;
          text-transform: uppercase;
          font-weight: 600;
          color: ${OLIVE};
        }
        .safety-head-title {
          font-family: ${FONT_DISP};
          font-size: clamp(1.4rem, 2.2vw, 1.85rem);
          font-weight: 500;
          color: ${EARTH};
          letter-spacing: -0.02em;
          line-height: 1.1;
          margin: 0;
        }
        .safety-toggle {
          width: 38px; height: 38px;
          border-radius: 50%;
          border: 1px solid ${EARTH_15};
          display: flex; align-items: center; justify-content: center;
          color: ${EARTH_70};
          flex-shrink: 0;
          transition: transform 360ms cubic-bezier(0.22,1,0.36,1),
                      border-color 280ms ease, color 280ms ease,
                      background 280ms ease;
        }
        .safety-card.active .safety-toggle {
          transform: rotate(45deg);
          background: ${LIME};
          border-color: ${LIME};
          color: ${EARTH};
        }
        .safety-toggle svg { display: block; }

        .safety-content { overflow: hidden; height: 0; }
        .safety-content-inner {
          padding: 0 clamp(20px, 2.4vw, 28px) clamp(24px, 3vh, 32px);
          display: grid;
          gap: clamp(20px, 2.6vw, 32px);
          grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
        }
        @media (max-width: 767px) {
          .safety-content-inner { grid-template-columns: 1fr; }
        }

        .safety-body {
          font-family: ${FONT_BODY};
          font-size: clamp(15px, 1.05vw, 17px);
          line-height: 1.7;
          color: ${EARTH_70};
          margin: 0 0 22px;
        }

        .safety-meter-row {
          display: flex; justify-content: space-between; align-items: baseline;
          font-family: ${FONT_BODY};
          font-size: 10px;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          font-weight: 600;
          color: ${OLIVE};
          margin-bottom: 10px;
        }
        .safety-meter-val {
          font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
          color: ${LIME};
          letter-spacing: 0.08em;
          font-size: 13px;
        }
        .safety-meter-bar {
          height: 4px;
          background: rgba(81,50,5,0.08);
          border-radius: 2px;
          overflow: hidden;
        }
        .safety-meter-bar > i {
          display: block;
          height: 100%;
          background: linear-gradient(to right, ${OLIVE} 0%, ${LIME} 100%);
          width: 0;
          transition: width 1100ms cubic-bezier(0.22,1,0.36,1);
          box-shadow: 0 0 8px rgba(159,175,8,0.6);
        }
        .safety-card.active .safety-meter-bar > i { width: 100%; }

        .safety-bullets {
          list-style: none;
          padding: 0; margin: 0;
          display: flex; flex-direction: column; gap: 12px;
        }
        .safety-bullet {
          display: flex; align-items: flex-start; gap: 12px;
          font-family: ${FONT_BODY};
          font-size: 13.5px;
          letter-spacing: 0.02em;
          color: ${EARTH_70};
          line-height: 1.5;
          opacity: 0;
          transform: translateX(-8px);
          transition: opacity 360ms ease, transform 420ms cubic-bezier(0.22,1,0.36,1);
        }
        .safety-bullet.show {
          opacity: 1;
          transform: translateX(0);
        }
        .safety-check {
          display: inline-flex;
          align-items: center; justify-content: center;
          width: 20px; height: 20px;
          border-radius: 50%;
          background: ${LIME};
          color: ${EARTH};
          flex-shrink: 0;
          font-size: 11px;
          margin-top: 1px;
          box-shadow: 0 0 0 4px rgba(159,175,8,0.10);
        }
        .safety-check svg { display: block; }

        @media (prefers-reduced-motion: reduce) {
          .safety-card, .safety-accent, .safety-toggle, .safety-meter-bar > i,
          .safety-bullet, .safety-head-icon { transition: none !important; }
        }
      `}</style>

      {pillars.map((p, i) => (
        <Card
          key={i}
          pillar={p}
          active={i === active}
          onToggle={() => setActive(active === i ? -1 : i)}
          reduced={reduced}
        />
      ))}
    </div>
  )
}

function Card({
  pillar,
  active,
  onToggle,
  reduced,
}: {
  pillar: PillarData
  active: boolean
  onToggle: () => void
  reduced: boolean
}) {
  const contentRef = useRef<HTMLDivElement>(null)
  const innerRef   = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState<number>(0)

  useEffect(() => {
    if (!contentRef.current || !innerRef.current) return
    const el = contentRef.current
    const target = active ? innerRef.current.scrollHeight : 0

    if (reduced) {
      el.style.height = active ? 'auto' : '0px'
      setShown(active ? pillar.bullets.length : 0)
      return
    }

    if (active) {
      el.style.height = el.offsetHeight + 'px'
      gsap.to(el, {
        height: target,
        duration: 0.55,
        ease: 'power3.out',
        onComplete: () => { el.style.height = 'auto' },
      })
      // Stagger bullets in
      setShown(0)
      let n = 0
      const tick = () => {
        n++
        setShown(n)
        if (n < pillar.bullets.length) {
          timer = window.setTimeout(tick, 110)
        }
      }
      let timer = window.setTimeout(tick, 240)
      return () => { window.clearTimeout(timer) }
    } else {
      gsap.to(el, {
        height: 0,
        duration: 0.4,
        ease: 'power2.inOut',
      })
      setShown(0)
    }
  }, [active, reduced, pillar.bullets.length])

  return (
    <div className={`safety-card ${active ? 'active' : ''}`}>
      <span className="safety-accent" aria-hidden="true" />
      <button
        type="button"
        onClick={onToggle}
        className="safety-head"
        aria-expanded={active}
      >
        <span className="safety-head-icon" aria-hidden="true">{pillar.icon}</span>
        <span className="safety-head-meta">
          <span className="safety-head-label">{pillar.label}</span>
          <span className="safety-head-title">{pillar.title}</span>
        </span>
        <span className="safety-toggle" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      <div ref={contentRef} className="safety-content">
        <div ref={innerRef} className="safety-content-inner">
          <div>
            <p className="safety-body">{pillar.body}</p>
            <div className="safety-meter-row">
              <span>{pillar.metricLabel}</span>
              <span className="safety-meter-val">{pillar.metric}</span>
            </div>
            <div className="safety-meter-bar"><i /></div>
          </div>
          <div>
            <ul className="safety-bullets">
              {pillar.bullets.map((b, i) => (
                <li key={i} className={`safety-bullet ${i < shown ? 'show' : ''}`}>
                  <span className="safety-check" aria-hidden="true">
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                      <path d="M2 5.5L4.5 8L9 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Approved Seal ──────────────────────────────────────────────────
// Circular SVG seal that rotates in on mount/scroll. Text runs along
// the inner circumference, "APPROVED ✓" sits in the middle. Good as
// the closing flourish of Section C.

interface SealProps {
  ringText: string
  centerLabel: string
  yearLabel: string
}
export function ApprovedSeal({ ringText, centerLabel, yearLabel }: SealProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  useEffect(() => {
    if (reduced || !wrapRef.current) return
    const node = wrapRef.current
    gsap.set(node, { autoAlpha: 0, scale: 0.7, rotation: -30 })
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          gsap.to(node, {
            autoAlpha: 1, scale: 1, rotation: 0,
            duration: 1.1, ease: 'power3.out',
          })
          obs.disconnect()
        }
      })
    }, { threshold: 0.4 })
    obs.observe(node)
    return () => obs.disconnect()
  }, [reduced])

  // Build the ring text (repeat with separators to fill the path)
  const ring = `${ringText} · ${ringText} · `

  return (
    <div ref={wrapRef} className="approved-seal" aria-hidden="true">
      <style>{`
        .approved-seal {
          width: clamp(180px, 22vw, 260px);
          aspect-ratio: 1;
          position: relative;
          margin: clamp(48px, 8vh, 80px) auto 0;
        }
        .approved-seal svg { width: 100%; height: 100%; display: block; overflow: visible; }
        @keyframes approved-seal-spin {
          from { transform: rotate(0deg);   }
          to   { transform: rotate(360deg); }
        }
        .approved-seal-ring {
          animation: approved-seal-spin 28s linear infinite;
          transform-origin: center;
        }
        .approved-seal-center {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          flex-direction: column;
          gap: 6px;
          text-align: center;
        }
        .approved-seal-tick {
          width: 32px; height: 32px;
          border-radius: 50%;
          background: ${LIME};
          color: ${EARTH};
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 6px;
          box-shadow: 0 0 0 6px rgba(159,175,8,0.16);
        }
        .approved-seal-label {
          font-family: ${FONT_BODY};
          font-size: 11px;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          font-weight: 700;
          color: ${EARTH};
        }
        .approved-seal-year {
          font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
          font-size: 10.5px;
          letter-spacing: 0.32em;
          color: ${EARTH_50};
          text-transform: uppercase;
        }
        @media (prefers-reduced-motion: reduce) {
          .approved-seal-ring { animation: none !important; }
        }
      `}</style>
      <svg viewBox="0 0 200 200">
        <defs>
          <path
            id="approved-seal-path"
            d="M 100,100 m -78,0 a 78,78 0 1,1 156,0 a 78,78 0 1,1 -156,0"
            fill="none"
          />
        </defs>
        <g className="approved-seal-ring">
          {/* Outer ring */}
          <circle cx="100" cy="100" r="92" fill="none" stroke={EARTH_15} strokeWidth="1" />
          <circle cx="100" cy="100" r="78" fill="none" stroke={LIME} strokeWidth="1.4" strokeDasharray="2 4" />
          <circle cx="100" cy="100" r="58" fill="none" stroke={EARTH_15} strokeWidth="1" />
          {/* Tick marks around the inner ring */}
          {Array.from({ length: 24 }).map((_, i) => {
            const a = (i / 24) * Math.PI * 2
            const x1 = 100 + Math.cos(a) * 72
            const y1 = 100 + Math.sin(a) * 72
            const x2 = 100 + Math.cos(a) * 76
            const y2 = 100 + Math.sin(a) * 76
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={OLIVE} strokeWidth="1" opacity="0.5" />
          })}
          {/* Curved text along the inner circumference */}
          <text
            style={{
              fontFamily: FONT_BODY,
              fontSize: 9,
              letterSpacing: '0.42em',
              textTransform: 'uppercase',
              fontWeight: 700,
              fill: OLIVE,
            }}
          >
            <textPath href="#approved-seal-path" startOffset="0">
              {ring + ring}
            </textPath>
          </text>
        </g>
      </svg>
      <div className="approved-seal-center">
        <span className="approved-seal-tick">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="approved-seal-label">{centerLabel}</span>
        <span className="approved-seal-year">{yearLabel}</span>
      </div>
    </div>
  )
}
